import { useCallback, useContext, useEffect, useMemo } from 'react';
import log from '@/lib/logger';
import { chargeCenterRoute } from '@/modules/charge-center/route';
import { UserSessionContext } from '@/providers/UserSessionContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { useLocation } from '@tanstack/react-router';
import ErrorBoundary from 'components/ErrorBoundary';
import OnboardingButton from 'components/OnboardingButton';
import SectionTitle from 'components/SectionTitle';
import ViewLoading from 'components/ViewLoading';
import ViewSectionMessage from 'components/ViewSectionMessage';
import { useAppDispatch, useAppSelector } from 'features';
import { assetsIsFetching, getAllAssetsById } from 'features/assets/selectors';
import { fetchAssets } from 'features/assets/thunks';
import { fetchChargePoints, unselectChargePoint, updateChargePoint } from 'features/ChargePoints';
import {
  fetchConnectorsByGridConnectionId,
  fetchOnlineConnectorsByGridConnectionId,
  fetchUpdatedConnector,
  unselectConnector,
} from 'features/Connectors';
import {
  fetchGridConnectionChargersPower,
  fetchGridConnectionMetersPower,
} from 'features/GridConnections';
import { getConnectorById } from 'features/selectors';
import { useFirstRender } from 'hooks/useFirstRender';
import useOnboarding from 'hooks/useOnboarding';
import { shallowEqual } from 'react-redux';
import { type Asset, type GridConnection } from 'types/swagger';
import IntlMessages from 'util/IntlMessages';

import ChargeCenterRouteButtons from './ChargeCenterRouteButtons';
import ChargePointMap from './ChargePointMap';
import GridConnectionsMonitor from './GridConnectionsMonitor';

const Title = () => (
  <div className="d-flex justify-content-between align-items-ceenter mb-3">
    <SectionTitle className="w-100" title={<IntlMessages id="pages.charge-center.subtitle-1" />} />
    <OnboardingButton />
  </div>
);

/**
 * ChargeCenter is the view that displays a list of assets with their grid gridConnections
 * and their information regarding charge status. This view is the one that makes fetch
 * of Assets and GridConnections, necessary to display the asset sections and their grid connections
 *
 * @returns JSX.Element
 */
const ChargeCenter = () => {
  const dispatch = useAppDispatch();
  const firstRender = useFirstRender();
  const { user, synclogWebsocket } = useContext(UserSessionContext);
  const assets = useAppSelector(getAllAssetsById, shallowEqual) as unknown as Asset[];
  const assetsLoading = useAppSelector(assetsIsFetching);
  const chargePointsByGridConnectionId = useAppSelector(
    (state) => state.chargePoints.byGridConnectionId,
    shallowEqual
  );

  const gridConnections = useMemo(
    () => assets?.map((asset) => asset.grid_connections as unknown as GridConnection).flat(),
    [assets]
  );

  useOnboarding(
    assetsLoading === 'success' || assetsLoading === 'error' ? 'charge_center_index' : '',
    null,
    null,
    800
  );

  const loadPowerValues = useCallback(
    async (gridConnectionId: number) => {
      await dispatch(fetchGridConnectionMetersPower(gridConnectionId));
      await dispatch(fetchGridConnectionChargersPower(gridConnectionId));
    },
    [dispatch]
  );

  const handleCloseDrawer = useCallback(() => {
    dispatch(unselectConnector());
    dispatch(unselectChargePoint());
  }, [dispatch]);

  const fetchChargeCenterConnectors = useCallback(async () => {
    await dispatch(fetchConnectorsByGridConnectionId(gridConnections[0].id));
    await dispatch(fetchOnlineConnectorsByGridConnectionId(gridConnections[0].id));
    gridConnections.forEach((gC, i) => {
      if (i === 0) return;
      void dispatch(fetchConnectorsByGridConnectionId(gC.id));
      void dispatch(fetchOnlineConnectorsByGridConnectionId(gC.id));
    });
  }, [dispatch, gridConnections]);

  const updatePowerValues = useCallback(async () => {
    if (!assets?.length) return;
    const gridConnectionIds: number[] = [];
    assets.forEach((asset) => {
      (asset.grid_connections as unknown as GridConnection[]).forEach((gridConnection) => {
        gridConnectionIds.push(gridConnection.id);
      });
    });
    if (!gridConnectionIds.length) return;
    await loadPowerValues(gridConnectionIds[0]);
    gridConnectionIds.forEach((gridConnectionId, i) => {
      if (i === 0) return;
      void loadPowerValues(gridConnectionId);
    });
  }, [assets, loadPowerValues]);

  const fetchChargeCenterData = useCallback(async () => {
    await dispatch(fetchAssets());
    await dispatch(fetchChargePoints());
    await updatePowerValues();
  }, [dispatch, updatePowerValues]);

  /**
   * The scope of the tabs migration is to remove the old Tabs component in favor of the new one.
   * This migration does not include changing the structure of the ChargeCenter component nor to migrate to the new layout structure.
   * Therefore, the tabs implementation here is kept as close to the original as possible.
   */

  const navigate = chargeCenterRoute.useNavigate();
  const location = useLocation();

  const currentTab: 'charge-center' | 'map' = location.pathname.includes('/charge-center/map')
    ? 'map'
    : 'charge-center';

  useEffect(() => {
    if (gridConnections?.length) void fetchChargeCenterConnectors();
  }, [gridConnections.length, fetchChargeCenterConnectors]);

  useEffect(() => {
    const interval = setInterval(() => {
      void updatePowerValues();
    }, 10000);
    return () => {
      clearInterval(interval);
    };
  }, [updatePowerValues]);

  useEffect(() => {
    window.addEventListener('popstate', handleCloseDrawer);
    return () => {
      window.removeEventListener('popstate', handleCloseDrawer);
    };
  }, [handleCloseDrawer]);

  useEffect(() => {
    if (!firstRender) return;
    void fetchChargeCenterData();
  }, [dispatch, fetchChargeCenterData, firstRender]);

  useEffect(() => {
    if (synclogWebsocket) {
      const newWebsocket = synclogWebsocket;
      if (newWebsocket.readyState === 1 && firstRender) {
        const subsMsg = {
          type: 'subscribe',
          topic: 'synclog',
        };
        newWebsocket.send(JSON.stringify(subsMsg));
      }
      newWebsocket.onmessage = async (event) => {
        const json = JSON.parse(event?.data ? event.data : undefined);
        if (json && json.type === 'authorization' && json.message === false) {
          newWebsocket.close();
        }

        if (
          json?.message.includes?.('/chargepoints/charge_points/') &&
          (json.message.includes('update') || json.message.includes('created'))
        ) {
          const messageData = JSON.parse(json.message);
          const newChargePoint = messageData.data;
          dispatch(updateChargePoint(newChargePoint));
        }

        if (
          json?.message.includes?.('/chargepoints/connectors/') &&
          (json.message.includes('update') || json.message.includes('created'))
        ) {
          const messageData = JSON.parse(json.message);
          const newConnector = messageData.data;
          void dispatch(fetchUpdatedConnector(newConnector));
        }

        if (
          json?.message.includes?.('/chargepoints/sampled_values/') &&
          (json.message.includes('update') || json.message.includes('created'))
        ) {
          const messageData = JSON.parse(json.message);
          const newSampledValue = messageData.data;
          try {
            const response = await getConnectorById(newSampledValue?.connector);
            if (response?.data) void dispatch(fetchUpdatedConnector(response.data));
          } catch (error) {
            log.error(error);
          }
        }
      };
    }

    return () => {
      if (synclogWebsocket) {
        const newWebsocket = synclogWebsocket;
        newWebsocket.onmessage = () => {};
      }
    };
  }, [dispatch, firstRender, synclogWebsocket, chargePointsByGridConnectionId]);

  if (assetsLoading === 'idle' || assetsLoading === 'pending') return <ViewLoading />;

  if (!user?.profile?.assets?.length && !user?.is_superuser) {
    return (
      <div className="h-100">
        <Title />
        <ViewSectionMessage message={<IntlMessages id="pages.charge-center.no-data" />} />
      </div>
    );
  }

  // if there are no assets
  if (!assets?.length) {
    return (
      <div className="h-100">
        <Title />
        <ViewSectionMessage message={<IntlMessages id="pages.charge-center.assets-not-found" />} />
      </div>
    );
  }

  // if assets have no grid connections
  if (!assets.filter((asset) => asset.grid_connections.length).length) {
    return (
      <div className="h-100">
        <Title />
        <ViewSectionMessage
          message={<IntlMessages id="pages.charge-center.assets-without-gridconnections" />}
        />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column flex-fill">
      <Title />
      <div className="d-flex flex-row justify-content-between align-items-start">
        <Tabs value={currentTab} className="$tw-w-full $tw-overflow-hidden $tw-rounded-lg">
          <TabsList className="$tw-bg-muted/50">
            <TabsTrigger
              value="charge-center"
              onClick={() => void navigate({ to: '/charge-center' })}
            >
              Monitor de empalmes
            </TabsTrigger>
            <TabsTrigger value="map" onClick={() => void navigate({ to: '/charge-center/map' })}>
              Mapa
            </TabsTrigger>
          </TabsList>
          <TabsContent value="charge-center">
            <ErrorBoundary>
              <ChargeCenterRouteButtons />
              <GridConnectionsMonitor />
            </ErrorBoundary>
          </TabsContent>
          <TabsContent value="map">
            <ErrorBoundary>
              <ChargePointMap />
            </ErrorBoundary>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ChargeCenter;
